/**
 * Turns a manual crop selection into the exact file that gets uploaded.
 *
 * The canvas is sized to the selection's own pixel dimensions rather than
 * resampled down to a fixed target — the crop is the only decision being
 * made here; how a document displays the result is that document's own
 * concern, not something baked into the file at crop time.
 */

export interface CroppedAreaPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("decode failed"));
    image.src = src;
  });

export async function cropImageToFile(
  imageSrc: string,
  area: CroppedAreaPixels,
  fileName: string
): Promise<{ file: File; dataUrl: string }> {
  const image = await loadImage(imageSrc);

  const width = Math.round(area.width);
  const height = Math.round(area.height);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not supported in this browser");

  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    width,
    height
  );

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png")
  );
  if (!blob) throw new Error("Could not export the cropped image");

  const croppedName = `${fileName.replace(/\.[^./\\]+$/, "")}.png`;
  const file = new File([blob], croppedName, { type: "image/png" });
  const dataUrl = canvas.toDataURL("image/png");

  return { file, dataUrl };
}
